
import React, { useState } from 'react';
import { Button } from './ui/Button';
import { X, Smartphone, Banknote, Loader2, CheckCircle, ShieldCheck, ArrowRight, AlertTriangle } from 'lucide-react';

interface PaymentModalProps {
  amount: number;
  workerName: string;
  workerUpiId?: string;
  onClose: () => void;
  onSuccess: (method: 'UPI' | 'CASH') => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({ amount, workerName, workerUpiId, onClose, onSuccess }) => {
  const [selectedMethod, setSelectedMethod] = useState<'UPI' | 'CASH'>('UPI');
  const [step, setStep] = useState<'SELECT' | 'CONFIRMING'>('SELECT');
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePay = async () => {
    if (selectedMethod === 'UPI') {
      if (!workerUpiId) {
        alert("This worker has not set up a UPI ID yet. Please pay via Cash.");
        return;
      }

      setIsProcessing(true);

      // Construct UPI Intent URL
      // tr=TransactionRef could be JobID if passed
      // pa = Payee Address (Worker UPI)
      // pn = Payee Name
      const upiUrl = `upi://pay?pa=${workerUpiId}&pn=${encodeURIComponent(workerName)}&am=${amount}&cu=INR`;

      // Redirect to UPI App
      window.location.href = upiUrl;

      // DO NOT Auto-Complete. Move to Confirmation Step.
      setTimeout(() => {
        setIsProcessing(false);
        setStep('CONFIRMING');
      }, 1000);

    } else {
      // Cash Payment - Still require manual 'Cash Given' confirmation or just 500ms delay?
      // User request focused on UPI verification. Cash is usually immediate handover.
      setIsProcessing(true);
      setTimeout(() => {
        onSuccess('CASH');
        onClose();
      }, 500);
    }
  };

  const handleConfirmUPI = () => {
    onSuccess('UPI');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-scaleIn">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" />
            Secure Payment
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-sm text-slate-500 mb-1">Total Amount Payable</p>
            <h1 className="text-4xl font-bold text-slate-900">₹{amount}</h1>
            <p className="text-xs text-blue-600 font-medium mt-2 bg-blue-50 inline-block px-2 py-1 rounded">Paying: {workerName}</p>
          </div>

          {step === 'SELECT' ? (
            <>
              <p className="text-xs font-bold text-slate-500 uppercase mb-3 ml-1">Select Payment Method</p>
              <div className="space-y-3 mb-8">

                {/* UPI Option */}
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedMethod === 'UPI' ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}>
                  <input type="radio" name="payment" className="hidden" checked={selectedMethod === 'UPI'} onChange={() => setSelectedMethod('UPI')} />
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors ${selectedMethod === 'UPI' ? 'bg-blue-100 text-blue-600' : 'bg-white text-slate-400'
                    }`}>
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <span className={`font-bold block ${selectedMethod === 'UPI' ? 'text-blue-900' : 'text-slate-900'}`}>Pay via UPI</span>
                    <span className="text-xs text-slate-500 block truncate">
                      {workerUpiId ? `To: ${workerUpiId}` : 'Google Pay, PhonePe, Paytm'}
                    </span>
                  </div>
                  {selectedMethod === 'UPI' && <div className="w-4 h-4 bg-blue-500 rounded-full shadow-sm ring-2 ring-white"></div>}
                </label>

                {/* Cash Option */}
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedMethod === 'CASH' ? 'border-green-500 bg-green-50' : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                  }`}>
                  <input type="radio" name="payment" className="hidden" checked={selectedMethod === 'CASH'} onChange={() => setSelectedMethod('CASH')} />
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors ${selectedMethod === 'CASH' ? 'bg-green-100 text-green-600' : 'bg-white text-slate-400'
                    }`}>
                    <Banknote className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <span className={`font-bold block ${selectedMethod === 'CASH' ? 'text-green-900' : 'text-slate-900'}`}>Cash</span>
                    <span className="text-xs text-slate-500">Pay cash after service</span>
                  </div>
                  {selectedMethod === 'CASH' && <div className="w-4 h-4 bg-green-500 rounded-full shadow-sm ring-2 ring-white"></div>}
                </label>
              </div>

              <Button fullWidth size="lg" onClick={handlePay} disabled={isProcessing} className="shadow-lg shadow-blue-500/30">
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...
                  </>
                ) : (
                  `Pay ₹${amount}`
                )}
              </Button>
            </>
          ) : (
            // CONFIRMATION STEP
            <div className="animate-slideUp text-center space-y-6">
              <div className="p-4 bg-amber-50 text-amber-800 rounded-xl border border-amber-100 text-sm font-bold flex items-start gap-3 text-left">
                <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                <div>
                  <p>Complete the payment in your UPI App.</p>
                  <p className="font-normal text-xs mt-1 text-amber-700">Only click 'I have Paid' after the transaction is successful.</p>
                </div>
              </div>

              <div className="p-4 border border-slate-100 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Paying To</p>
                <p className="font-black text-slate-900">{workerUpiId}</p>
              </div>

              <div className="space-y-3">
                <Button fullWidth size="lg" onClick={handleConfirmUPI} className="bg-green-600 hover:bg-green-700 h-14 shadow-lg shadow-green-600/20">
                  <CheckCircle className="w-5 h-5 mr-2" /> Yes, I have Paid
                </Button>
                <Button fullWidth variant="ghost" onClick={() => setStep('SELECT')} className="text-slate-500">
                  Retry Payment
                </Button>
              </div>
            </div>
          )}

          <p className="text-center text-[10px] text-slate-400 mt-4 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Payments are secure and encrypted
          </p>
        </div>
      </div>
    </div>
  );
};
